import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Users2, Hash, KeyRound, Megaphone, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { SegmentAdminPicker, SegmentAddressInput } from "../../components/SegmentAdminFormHelpers";

function CodesDialog({ segment, onClose }) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [count, setCount] = useState(100);
  const [data, setData] = useState({ total: 0, redeemed: 0, available: 0, codes: [] });
  const [justGenerated, setJustGenerated] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/segments/${segment.segment_id}/codes`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [segment.segment_id]);

  const generate = async () => {
    setGenerating(true);
    setJustGenerated([]);
    try {
      const r = await fetch(`${API}/admin/segments/${segment.segment_id}/codes/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ count: parseInt(count, 10) || 1 }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      const payload = await r.json();
      setJustGenerated(payload.codes || []);
      toast.success(`Generated ${payload.count} codes`);
      load();
    } catch (e) {
      toast.error(e.message || "Failed to generate");
    } finally { setGenerating(false); }
  };

  const copyAll = () => {
    const text = (justGenerated.length ? justGenerated : data.codes).map((c) => c.code).join("\n");
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
  };

  const downloadCsv = () => {
    const list = justGenerated.length ? justGenerated : data.codes;
    const rows = [["code", "is_redeemed", "redeemed_by_email", "redeemed_at", "created_at"]];
    list.forEach((c) => rows.push([c.code, c.is_redeemed ? "yes" : "no", c.redeemed_by_email || "", c.redeemed_at || "", c.created_at || ""]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${segment.name}-codes.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const removeCode = async (code_id) => {
    if (!confirm("Delete this code? Users holding this code will no longer be able to redeem it.")) return;
    try {
      const r = await fetch(`${API}/admin/segments/${segment.segment_id}/codes/${code_id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error();
      toast.success("Code deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Codes — {segment.name}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Total</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{data.total}</p>
          </div>
          <div className="rounded-xl bg-green-50 border border-green-100 p-3">
            <p className="text-[10px] uppercase tracking-wider text-green-600 font-semibold">Available</p>
            <p className="text-2xl font-bold text-green-700 mt-1">{data.available}</p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3">
            <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">Redeemed</p>
            <p className="text-2xl font-bold text-amber-700 mt-1">{data.redeemed}</p>
          </div>
        </div>

        <div className="flex items-end gap-2 mb-5">
          <div className="flex-1">
            <Label className="text-xs text-gray-500">Generate codes</Label>
            <Input type="number" min={1} max={1000} value={count} onChange={(e) => setCount(e.target.value)} placeholder="100" data-testid="codes-count" />
          </div>
          <Button onClick={generate} disabled={generating} className="bg-green-600 hover:bg-green-700" data-testid="codes-generate-btn">
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Plus size={14} className="mr-1" /> Generate</>}
          </Button>
        </div>

        {justGenerated.length > 0 && (
          <div className="rounded-xl border border-green-200 bg-green-50 p-3 mb-4" data-testid="just-generated-codes">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-green-800">Just generated — save these codes now, you can list them again below.</p>
              <div className="flex gap-1">
                <button onClick={copyAll} className="text-[10px] px-2 py-1 rounded-md bg-white text-green-700 hover:bg-white/70 font-semibold" data-testid="codes-copy">
                  <Copy size={11} className="inline mr-1" /> Copy
                </button>
                <button onClick={downloadCsv} className="text-[10px] px-2 py-1 rounded-md bg-white text-green-700 hover:bg-white/70 font-semibold" data-testid="codes-download">
                  <Download size={11} className="inline mr-1" /> CSV
                </button>
              </div>
            </div>
            <div className="font-mono text-xs grid grid-cols-4 md:grid-cols-6 gap-1">
              {justGenerated.map((c) => (
                <span key={c.code_id} className="bg-white rounded px-2 py-1 text-center border border-green-200">{c.code}</span>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">All codes</p>
            {data.codes.length > 0 && (
              <button onClick={downloadCsv} className="text-[10px] px-2 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 font-semibold">
                <Download size={11} className="inline mr-1" /> Export CSV
              </button>
            )}
          </div>
          {loading ? (
            <p className="text-xs text-gray-400 py-4 text-center">Loading…</p>
          ) : data.codes.length === 0 ? (
            <p className="text-xs text-gray-400 py-6 text-center italic">No codes yet. Generate some above.</p>
          ) : (
            <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50" data-testid="codes-list">
              {data.codes.map((c) => (
                <div key={c.code_id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`code-row-${c.code_id}`}>
                  <span className="font-mono font-semibold text-gray-800">{c.code}</span>
                  <div className="flex items-center gap-3">
                    {c.is_redeemed ? (
                      <span className="text-[10px] font-semibold text-amber-700 truncate max-w-[180px]" title={c.redeemed_by_email || c.redeemed_by_name}>
                        Redeemed by {c.redeemed_by_email || c.redeemed_by_name || "a customer"}
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold text-green-700">Available</span>
                    )}
                    {!c.is_redeemed && (
                      <button onClick={() => removeCode(c.code_id)} className="text-gray-300 hover:text-red-500" title="Delete" data-testid={`code-delete-${c.code_id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDialog({ segment, onClose }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [url, setUrl] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Title is required"); return; }
    const targetLabel = segment.segment_id ? segment.name : "all customers";
    if (!confirm(`Send this notification to ${targetLabel}?`)) return;
    setSending(true);
    try {
      const body = {
        title: title.trim(),
        message: message.trim(),
        url: url.trim() || undefined,
      };
      if (segment.segment_id) body.segment_id = segment.segment_id;
      const r = await fetch(`${API}/admin/broadcast`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      const payload = await r.json();
      toast.success(`Delivered to ${payload.delivered_count} of ${payload.target_count} customers`);
      onClose?.();
    } catch (e2) {
      toast.error(e2.message || "Failed to send");
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Megaphone size={18} /> Broadcast — {segment.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={send} className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New TCS menu drop" required data-testid="broadcast-title" />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What would you like to tell customers in this segment?" rows={4} data-testid="broadcast-message" />
          </div>
          <div>
            <Label>Link (optional)</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/menu  or  https://…" data-testid="broadcast-url" />
          </div>
          <Button type="submit" disabled={sending} className="w-full bg-green-600 hover:bg-green-700" data-testid="broadcast-send-btn">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Megaphone size={16} className="mr-2" /> Send to {segment.name}</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSegments() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
    admin_user_id: "",
    admin_user_label: "",
    default_shipping_address: null,
    min_order_value: 0,
    min_order_value_pickup: "",
    packaging_pct: "",
  });
  const [saving, setSaving] = useState(false);
  const [codesFor, setCodesFor] = useState(null);
  const [broadcastFor, setBroadcastFor] = useState(null);

  const fetchAll = async () => {
    try {
      const r = await fetch(`${API}/admin/segments`, { credentials: "include" });
      if (r.ok) setSegments(await r.json());
    } catch {
      toast.error("Failed to load segments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", admin_user_id: "", admin_user_label: "", default_shipping_address: null, min_order_value: 0, min_order_value_pickup: "", packaging_pct: "" });
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      description: s.description || "",
      admin_user_id: s.admin_user_id || "",
      admin_user_label: s.admin_user_email || "",
      default_shipping_address: s.default_shipping_address || null,
      min_order_value: Number(s.min_order_value || 0),
      min_order_value_pickup: s.min_order_value_pickup === null || s.min_order_value_pickup === undefined ? "" : Number(s.min_order_value_pickup),
      packaging_pct: s.packaging_pct === null || s.packaging_pct === undefined ? "" : Number(s.packaging_pct),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const url = editing ? `${API}/admin/segments/${editing.segment_id}` : `${API}/admin/segments`;
      const method = editing ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: form.name.trim(),
          description: form.description.trim(),
          admin_user_id: form.admin_user_id || null,
          default_shipping_address: form.default_shipping_address || null,
          min_order_value: Number(form.min_order_value) || 0,
          min_order_value_pickup: form.min_order_value_pickup === "" || form.min_order_value_pickup === null ? null : Number(form.min_order_value_pickup),
          packaging_pct: form.packaging_pct === "" || form.packaging_pct === null ? null : Number(form.packaging_pct),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success(editing ? "Segment updated" : "Segment created");
      setOpen(false);
      fetchAll();
    } catch (e2) {
      toast.error(e2.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this segment? It will be removed from all customers, kitchens, plans and cobranding entries.")) return;
    try {
      const r = await fetch(`${API}/admin/segments/${id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error();
      toast.success("Segment deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-segments">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Customer Segments</h1>
            <p className="text-sm text-gray-500 mt-0.5">Group customers to control kitchen, plan and cobranding visibility.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setBroadcastFor({ segment_id: "", name: "All Customers" })}
              data-testid="broadcast-all-btn"
            >
              <Megaphone size={16} className="mr-2" /> Broadcast to All
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700" onClick={openCreate} data-testid="add-segment-btn">
                <Plus size={18} className="mr-2" /> Add Segment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Segment" : "New Customer Segment"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. TCS Employees" required data-testid="segment-name" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional note for admins" rows={2} data-testid="segment-description" />
                </div>

                {/* Segment Admin picker */}
                <SegmentAdminPicker
                  value={form.admin_user_id}
                  label={form.admin_user_label}
                  onChange={(id, lbl) => setForm({ ...form, admin_user_id: id, admin_user_label: lbl })}
                />

                {/* Default Shipping Address */}
                <SegmentAddressInput
                  value={form.default_shipping_address}
                  onChange={(addr) => setForm({ ...form, default_shipping_address: addr })}
                />

                {/* Minimum Order Value — Delivery */}
                <div>
                  <Label>Minimum Order Value — Delivery</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.min_order_value}
                    onChange={(e) => setForm({ ...form, min_order_value: e.target.value })}
                    placeholder="0"
                    data-testid="segment-min-order-value"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Customers in this segment must spend at least this amount (after discount, before tax) to place a <span className="font-semibold">delivery</span> order. Leave 0 to disable.</p>
                </div>
                {/* Minimum Order Value — Pickup */}
                <div>
                  <Label>Minimum Order Value — Pickup</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.min_order_value_pickup}
                    onChange={(e) => setForm({ ...form, min_order_value_pickup: e.target.value })}
                    placeholder="Same as delivery"
                    data-testid="segment-min-order-value-pickup"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Separate threshold for <span className="font-semibold">pickup</span> orders. Leave blank to fall back to the delivery value.</p>
                </div>
                {/* Packaging & Handling % */}
                <div>
                  <Label>Packaging &amp; Handling (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.packaging_pct}
                    onChange={(e) => setForm({ ...form, packaging_pct: e.target.value })}
                    placeholder="Leave blank to use global rate"
                    data-testid="segment-packaging-pct"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Applied on delivery orders (after discount, before tax). Leave blank to inherit the global rate. If the user belongs to multiple segments, the HIGHEST rate wins.</p>
                </div>
                <Button type="submit" disabled={saving} className="w-full bg-green-600 hover:bg-green-700" data-testid="segment-save">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : editing ? "Update Segment" : "Create Segment"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
        ) : segments.length === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-100 rounded-2xl">
            <Users2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No segments yet</p>
            <p className="text-xs text-gray-400 mt-1">Create your first segment to start restricting content visibility.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {segments.map((s) => (
              <div key={s.segment_id} className="bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-sm transition-shadow" data-testid={`segment-card-${s.segment_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-bold text-gray-900 text-base truncate">{s.name}</h3>
                    {s.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{s.description}</p>}
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-3">
                      <Hash size={11} /> {s.member_count} member{s.member_count === 1 ? "" : "s"}
                    </div>
                    {s.admin_user_email && (
                      <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 mt-1">
                        <Users2 size={11} /> SA: <span className="truncate font-medium">{s.admin_user_email}</span>
                      </div>
                    )}
                    {s.default_shipping_address && (
                      <div className="flex items-center gap-1.5 text-[10px] text-amber-600 mt-1 font-medium">
                        <KeyRound size={10} /> Locked address: {(s.default_shipping_address.city || "set")}
                      </div>
                    )}
                    {Number(s.min_order_value || 0) > 0 && (
                      <div className="flex items-center gap-1.5 text-[10px] text-blue-600 mt-1 font-medium" data-testid={`segment-mov-${s.segment_id}`}>
                        <Hash size={10} /> Min order: ₹{Number(s.min_order_value)} (delivery)
                        {s.min_order_value_pickup !== null && s.min_order_value_pickup !== undefined && (
                          <span>· ₹{Number(s.min_order_value_pickup)} (pickup)</span>
                        )}
                      </div>
                    )}
                    {s.packaging_pct !== null && s.packaging_pct !== undefined && (
                      <div className="flex items-center gap-1.5 text-[10px] text-purple-600 mt-1 font-medium" data-testid={`segment-packaging-${s.segment_id}`}>
                        <Hash size={10} /> Packaging: {Number(s.packaging_pct)}%
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setCodesFor(s)} className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-50" title="Manage codes" data-testid={`codes-segment-${s.segment_id}`}>
                      <KeyRound size={14} />
                    </button>
                    <button onClick={() => setBroadcastFor(s)} className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-50" title="Broadcast" data-testid={`broadcast-segment-${s.segment_id}`}>
                      <Megaphone size={14} />
                    </button>
                    <button onClick={() => openEdit(s)} className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-50" data-testid={`edit-segment-${s.segment_id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(s.segment_id)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-50" data-testid={`delete-segment-${s.segment_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {codesFor && <CodesDialog segment={codesFor} onClose={() => setCodesFor(null)} />}
      {broadcastFor && <BroadcastDialog segment={broadcastFor} onClose={() => setBroadcastFor(null)} />}
    </AdminLayout>
  );
}
